import { Output } from "valibot";

import { LocaleSchema } from "shared/schemas";
import { Literals } from "shared/types";

export const hexToRGBA = (hex: string, alpha = 1) => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);

  return `rgba(${r},${g},${b},${alpha})`;
};

export const getTypeIcon = (url: string) => {
  const splittedUrl = url.split(".");
  const type = splittedUrl.pop()?.toLowerCase();
  const baseUrl = "https://writesonic.s3.amazonaws.com/frontend-assets/icons/";
  switch (type) {
    case "pdf": {
      return baseUrl + "pdf.png";
    }
    case "docx": {
      return baseUrl + "doc.png";
    }
    case "pptx": {
      return baseUrl + "ppt.png";
    }
    default: {
      return baseUrl + "link.png";
    }
  }
};

const awsPrefix = "botsonic.s3.amazonaws.com/";
export const getSourceName = (url: string) => {
  try {
    if (url.includes(awsPrefix)) {
      const queryStringStart = url.indexOf("?");
      const pathWithQuery =
        queryStringStart === -1
          ? url
          : url.slice(0, Math.max(0, queryStringStart));
      const pathComponents = pathWithQuery.split("/");
      const filenameWithExtension = pathComponents.at(-1);
      const filenameParts = filenameWithExtension?.split(".");
      filenameParts?.shift(); // Remove the UUID portion
      filenameParts?.pop(); // Remove the extension
      return filenameParts?.join(".") ?? url;
    }

    return url.replace("https://", "");
  } catch {
    return url;
  }
};

export function isFileUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch {
    return false;
  }
  return (
    (url.protocol === "https:" || url.protocol === "http:") &&
    (url.pathname.endsWith(".pdf") ||
      url.pathname.endsWith(".docx") ||
      url.pathname.endsWith(".doc") ||
      url.pathname.endsWith(".pptx") ||
      url.pathname.endsWith(".ppt") ||
      url.pathname.endsWith(".xlsx") ||
      url.pathname.endsWith(".xls") ||
      url.pathname.endsWith(".csv"))
  );
}

/* eslint-disable max-len */
export const getChatPanelText = (
  language: Output<typeof LocaleSchema>,
): Literals => {
  switch (language) {
    case "es":
      return {
        start_again: "Reiniciar",
        default_company_name: "Nombre de la Empresa",

        default_chatbot_subheading: "Nuestro bot responde al instante",
        input_placeholder: "Envía un mensaje...",
        bottom_powered_by: "Desarrollado por",
        bottom_text_cta: "Construye tu propio bot de IA con",
        default_welcome_message: "Hola, ¿en qué puedo ayudarte?",
        resetModalTitle: "¿Estás seguro de que\nquieres terminar este chat?",
        endChatButton: "Sí, termina este chat",
        goBackButton: "No, regresa",
        endModalTitle: "¿Pudimos resolver tu problema?",
        yes: "Sí",
        no: "No",
        submitButton: "Enviar",
        skipFeedbackButton: "Omitir",
        additionalFeedbackPlaceholder: "Añade comentarios adicionales aquí...",
        consentAgreementAcceptButton: "Aceptar e iniciar",
        sourcesText: "Fuentes",
        submittingButtonText: "Enviando...",
      };

    case "fr":
      return {
        start_again: "Recommencer",
        default_company_name: "Nom de l'Entreprise",
        default_chatbot_subheading: "Notre bot répond instantanément",
        input_placeholder: "Envoyer un message...",
        bottom_powered_by: "Propulsé par",
        bottom_text_cta: "Créez votre propre bot AI avec",
        default_welcome_message: "Bonjour, comment puis-je vous aider ?",
        resetModalTitle: "Êtes-vous sûr de vouloir\nmettre fin à ce chat ?",
        endChatButton: "Oui, terminer ce chat",
        goBackButton: "Non, revenir en arrière",
        endModalTitle: "Avons-nous pu résoudre votre problème?",
        yes: "Oui",
        no: "Non",
        submitButton: "Soumettre",
        skipFeedbackButton: "Passer",
        additionalFeedbackPlaceholder:
          "Ajoutez des commentaires supplémentaires ici...",
        consentAgreementAcceptButton: "Accepter et commencer",
        sourcesText: "Sources",
        submittingButtonText: "Envoi en cours...",
      };

    case "de":
      return {
        start_again: "Neustart",
        default_company_name: "Firmenname",
        default_chatbot_subheading: "Unser Bot antwortet sofort",
        input_placeholder: "Nachricht senden...",
        bottom_powered_by: "Angetrieben von",
        bottom_text_cta: "Erstellen Sie Ihren eigenen KI-Bot mit",
        default_welcome_message: "Hallo, wie kann ich Ihnen helfen?",
        resetModalTitle:
          "Sind Sie sicher, dass Sie\ndiesen Chat beenden möchten?",
        endChatButton: "Ja, diesen Chat beenden",
        goBackButton: "Nein, zurück",
        endModalTitle: "Konnten wir Ihr Problem lösen?",
        yes: "Ja",
        no: "Nein",
        submitButton: "Absenden",
        skipFeedbackButton: "Überspringen",
        additionalFeedbackPlaceholder:
          "Fügen Sie hier zusätzliche Kommentare hinzu...",
        consentAgreementAcceptButton: "Akzeptieren und starten",
        sourcesText: "Quellen",
        submittingButtonText: "Sendet...",
      };

    case "it":
      return {
        start_again: "Ricomincia",
        default_company_name: "Nome dell'azienda",
        default_chatbot_subheading: "Il nostro bot risponde istantaneamente",
        input_placeholder: "Invia un messaggio...",
        bottom_powered_by: "Offerto da",
        bottom_text_cta: "La tua IA personalizzata con",
        default_welcome_message: "Ciao, come posso aiutarti?",
        resetModalTitle: "Sei sicuro di voler\ninterrompere questa chat?",
        endChatButton: "Sì, termina questa chat",
        goBackButton: "No, torna indietro",
        endModalTitle: "Siamo riusciti a risolvere il tuo problema?",
        yes: "Sì",
        no: "No",
        submitButton: "Invia",
        skipFeedbackButton: "Salta",
        additionalFeedbackPlaceholder: "Aggiungi commenti aggiuntivi qui...",
        consentAgreementAcceptButton: "Accetta e inizia",
        sourcesText: "Fonti",
        submittingButtonText: "Sto inviando...",
      };

    case "nl":
      return {
        start_again: "Opnieuw beginnen",
        default_company_name: "Bedrijfsnaam",
        default_chatbot_subheading: "Onze bot beantwoordt direct",
        input_placeholder: "Stuur een bericht...",
        bottom_powered_by: "Aangeboden door",
        bottom_text_cta: "Bouw je eigen AI-bot met",
        default_welcome_message: "Hallo, hoe kan ik je helpen?",
        resetModalTitle: "Weet je zeker dat je\ndeze chat wilt beëindigen?",
        endChatButton: "Ja, beëindig deze chat",
        goBackButton: "Nee, ga terug",
        endModalTitle: "Hebben we uw probleem kunnen oplossen?",
        yes: "Ja",
        no: "Nee",
        submitButton: "Verzenden",
        skipFeedbackButton: "Overslaan",
        additionalFeedbackPlaceholder: "Voeg hier extra opmerkingen toe...",
        consentAgreementAcceptButton: "Accepteren en beginnen",
        sourcesText: "Bronnen",
        submittingButtonText: "Versturen...",
      };

    case "pl":
      return {
        start_again: "Zacznij od nowa",
        default_company_name: "Nazwa firmy",
        default_chatbot_subheading: "Nasz bot odpowiada natychmiastowo",
        input_placeholder: "Wyślij wiadomość...",
        bottom_powered_by: "Oferowane przez",
        bottom_text_cta: "Zbuduj swego własnego bota AI z",
        default_welcome_message: "Cześć, w czym mogę pomóc?",
        resetModalTitle: "Czy na pewno chcesz\nzakończyć ten czat?",
        endChatButton: "Tak, zakończ ten czat",
        goBackButton: "Nie, wróć",
        endModalTitle: "Czy udało nam się rozwiązać twój problem?",
        yes: "Tak",
        no: "Nie",
        submitButton: "Wyślij",
        skipFeedbackButton: "Pomiń",
        additionalFeedbackPlaceholder: "Dodaj dodatkowe komentarze tutaj...",
        consentAgreementAcceptButton: "Zaakceptuj i zacznij",
        sourcesText: "Źródła",
        submittingButtonText: "Wysyłanie...",
      };

    case "ru":
      return {
        start_again: "Начать заново",
        default_company_name: "Название компании",
        default_chatbot_subheading: "Наш бот отвечает мгновенно",
        input_placeholder: "Отправить сообщение...",
        bottom_powered_by: "Предоставлено",
        bottom_text_cta: "Создайте своего бота с ИИ",
        default_welcome_message: "Здравствуйте, чем я могу вам помочь?",
        resetModalTitle: "Вы уверены, что хотите\nзавершить этот чат?",
        endChatButton: "Да, завершить этот чат",
        goBackButton: "Нет, вернуться назад",
        endModalTitle: "Смогли ли мы решить вашу проблему?",
        yes: "Да",
        no: "Нет",
        submitButton: "Отправить",
        skipFeedbackButton: "Пропустить",
        additionalFeedbackPlaceholder:
          "Добавьте дополнительные комментарии здесь...",
        consentAgreementAcceptButton: "Принять и начать",
        sourcesText: "Источники",
        submittingButtonText: "Идёт отправка...",
      };

    case "ja":
      return {
        start_again: "最初からやり直す",
        default_company_name: "会社名",
        default_chatbot_subheading: "私たちのボットは即座に応答します",
        input_placeholder: "メッセージを送信...",
        bottom_powered_by: "提供元",
        bottom_text_cta: "自分自身のAIボットを作成する",
        default_welcome_message: "こんにちは、どういったご質問でしょうか？",
        resetModalTitle: "このチャットを終了しても\nよろしいですか？",
        endChatButton: "はい、このチャットを終了する",
        goBackButton: "いいえ、戻る",
        endModalTitle: "あなたの問題を解決できましたか？",
        yes: "はい",
        no: "いいえ",
        submitButton: "送信",
        skipFeedbackButton: "スキップ",
        additionalFeedbackPlaceholder:
          "ここに追加のコメントを入力してください...",
        consentAgreementAcceptButton: "受け入れて開始する",
        sourcesText: "情報源",
        submittingButtonText: "送信中...",
      };

    case "zh":
      return {
        start_again: "重新开始",
        default_company_name: "公司名称",
        default_chatbot_subheading: "我们的机器人即时回答",
        input_placeholder: "发送消息...",
        bottom_powered_by: "由...提供",
        bottom_text_cta: "使用AI创建您自己的机器人",
        default_welcome_message: "您好，我能帮您什么忙吗？",
        resetModalTitle: "您确定要\n结束此聊天吗？",
        endChatButton: "是的，结束这个聊天",
        goBackButton: "不，返回",
        endModalTitle: "我们能帮您解决问题吗？",
        yes: "是",
        no: "否",
        submitButton: "提交",
        skipFeedbackButton: "跳过",
        additionalFeedbackPlaceholder: "在此处添加额外的评论...",
        consentAgreementAcceptButton: "接受并开始",
        sourcesText: "来源",
        submittingButtonText: "提交中...",
      };

    case "bg":
      return {
        start_again: "Ново начало",
        default_company_name: "Име на компанията",
        default_chatbot_subheading: "Нашето бот реагира веднага",
        input_placeholder: "Изпратете съобщение...",
        bottom_powered_by: "Предоставено от",
        bottom_text_cta: "Създайте своя собствен AI бот с",
        default_welcome_message: "Здравейте, как мога да помогна?",
        resetModalTitle: "Сигурни ли сте, че искате\nда прекратите този чат?",
        endChatButton: "Да, прекратете този чат",
        goBackButton: "Не, върнете се",
        endModalTitle: "Успяхме ли да решим проблема ви?",
        yes: "Да",
        no: "Не",
        submitButton: "Изпрати",
        skipFeedbackButton: "Пропусни",
        additionalFeedbackPlaceholder: "Добавете допълнителни коментари тук...",
        consentAgreementAcceptButton: "Приеми и започни",
        sourcesText: "Източници",
        submittingButtonText: "Изпращане...",
      };

    case "cs":
      return {
        start_again: "Začít znovu",
        default_company_name: "Název společnosti",
        default_chatbot_subheading: "Náš chatbot reaguje okamžitě",
        input_placeholder: "Pošlete zprávu...",
        bottom_powered_by: "Poháněno",
        bottom_text_cta:
          "Sestavte si svého vlastního robota s umělou inteligencí",
        default_welcome_message: "Ahoj, jak vám mohu pomoci?",
        resetModalTitle: "Jste si jisti, že chcete\ntento chat ukončit?",
        endChatButton: "Ano, ukončete tento chat",
        goBackButton: "Ne, vraťte se",
        endModalTitle: "Podarilo se nám vyřešit váš problém?",
        yes: "Ano",
        no: "Ne",
        submitButton: "Odeslat",
        skipFeedbackButton: "Přeskočit",
        additionalFeedbackPlaceholder: "Přidejte další komentáře zde...",
        consentAgreementAcceptButton: "Přijmout a začít",
        sourcesText: "Zdroje",
        submittingButtonText: "Odesílání...",
      };

    case "da":
      return {
        start_again: "Start forfra",
        default_company_name: "Virksomhedsnavn",
        default_chatbot_subheading: "Vores chatbot svarer øjeblikkeligt",
        input_placeholder: "Send en besked...",
        bottom_powered_by: "Leveret af",
        bottom_text_cta: "Byg din egen AI-bot med",
        default_welcome_message: "Hej, hvordan kan jeg hjælpe dig?",
        resetModalTitle: "Er du sikker på, at du vil\nafslutte denne chat?",
        endChatButton: "Ja, afslut denne chat",
        goBackButton: "Nej, gå tilbage",
        endModalTitle: "Kunne vi hjælpe med at løse dit problem?",
        yes: "Ja",
        no: "Nej",
        submitButton: "Indsend",
        skipFeedbackButton: "Spring over",
        additionalFeedbackPlaceholder: "Tilføj yderligere kommentarer her...",
        consentAgreementAcceptButton: "Accepter og start",
        sourcesText: "Kilder",
        submittingButtonText: "Sender...",
      };

    case "el":
      return {
        start_again: "Επανεκκίνηση",
        default_company_name: "Όνομα Εταιρείας",
        default_chatbot_subheading: "Το bot μας απαντά αμέσως",
        input_placeholder: "Αποστολή μηνύματος...",
        bottom_powered_by: "Παρέχεται από",
        bottom_text_cta: "Φτιάξτε το δικό σας bot AI με",
        default_welcome_message: "Γεια σας, πώς μπορώ να σας βοηθήσω;",
        resetModalTitle:
          "Είστε σίγουροι ότι θέλετε\nνα τερματίσετε αυτή τη συνομιλία;",
        endChatButton: "Ναι, τερματίστε αυτή τη συνομιλία",
        goBackButton: "Όχι, επιστροφή",
        endModalTitle: "Μπορέσαμε να λύσουμε το πρόβλημά σας;",
        yes: "Ναι",
        no: "Όχι",
        submitButton: "Υποβολή",
        skipFeedbackButton: "Παράλειψη",
        additionalFeedbackPlaceholder: "Προσθέστε επιπλέον σχόλια εδώ...",
        consentAgreementAcceptButton: "Αποδοχή και έναρξη",
        sourcesText: "Πηγές",
        submittingButtonText: "Υποβολή...",
      };

    case "hu":
      return {
        start_again: "Újrakezdés",
        default_company_name: "Cégnév",
        default_chatbot_subheading: "A botunk azonnal válaszol",
        input_placeholder: "Üzenet küldése...",
        bottom_powered_by: "Ajánlja",
        bottom_text_cta: "Építsd meg saját AI botodat ezzel",
        default_welcome_message: "Szia, miben segíthetek?",
        resetModalTitle: "Biztosan be szeretné\nfejezni ezt a csevegést?",
        endChatButton: "Igen, befejezem ezt a csevegést",
        goBackButton: "Nem, vissza",
        endModalTitle: "Sikerült megoldanunk a problémádat?",
        yes: "Igen",
        no: "Nem",
        submitButton: "Beküldés",
        skipFeedbackButton: "Átugrás",
        additionalFeedbackPlaceholder:
          "Ide írja be a további megjegyzéseket...",
        consentAgreementAcceptButton: "Elfogadás és indítás",
        sourcesText: "Források",
        submittingButtonText: "Beküldés...",
      };

    case "lt":
      return {
        start_again: "Pradėti iš naujo",
        default_company_name: "Įmonės pavadinimas",
        default_chatbot_subheading: "Mūsų robotas atsako akimirksniu",
        input_placeholder: "Siųsti žinutę...",
        bottom_powered_by: "Suteikia",
        bottom_text_cta: "Sukurkite savo AI botą naudodami",
        default_welcome_message: "Labas, kuo galiu padėti?",
        resetModalTitle: "Ar tikrai norite\nbaigti šį pokalbį?",
        endChatButton: "Taip, baigti šį pokalbį",
        goBackButton: "Ne, grįžti atgal",
        endModalTitle: "Ar mums pavyko išspręsti jūsų problemą?",
        yes: "Taip",
        no: "Ne",
        submitButton: "Pateikti",
        skipFeedbackButton: "Praleisti",
        additionalFeedbackPlaceholder: "Pridėkite papildomų komentarų čia...",
        consentAgreementAcceptButton: "Priimti ir pradėti",
        sourcesText: "Šaltiniai",
        submittingButtonText: "Pateikiamas...",
      };

    case "lv":
      return {
        start_again: "Sākt no jauna",
        default_company_name: "Uzņēmuma nosaukums",
        default_chatbot_subheading:
          "Mūsu bots atbild uz jūsu jautājumiem nekavējoties",
        input_placeholder: "Sūtīt ziņu...",
        bottom_powered_by: "Nodrošina",
        bottom_text_cta: "Izveidojiet savu pašu AI botu ar",
        default_welcome_message: "Sveiki, kā es varu Jums palīdzēt?",
        resetModalTitle: "Vai tiešām vēlaties\nbeigt šo tērzēšanu?",
        endChatButton: "Jā, beigt šo tērzēšanu",
        goBackButton: "Nē, doties atpakaļ",
        endModalTitle: "Vai mēs spējām atrisināt jūsu problēmu?",
        yes: "Jā",
        no: "Nē",
        submitButton: "Iesniegt",
        skipFeedbackButton: "Izlaist",
        additionalFeedbackPlaceholder:
          "Pievienojiet papildu komentārus šeit...",
        consentAgreementAcceptButton: "Pieņemt un sākt",
        sourcesText: "Avoti",
        submittingButtonText: "Iesniedz...",
      };

    case "ro":
      return {
        start_again: "Începe din nou",
        default_company_name: "Numele Companiei",
        default_chatbot_subheading: "Botul nostru răspunde instantaneu",
        input_placeholder: "Trimite un mesaj...",
        bottom_powered_by: "Oferit de",
        bottom_text_cta: "Construiește-ți propriul bot AI cu",
        default_welcome_message: "Salut, cum vă pot ajuta?",
        resetModalTitle:
          "Sunteți sigur că doriți\nsă încheiați această conversație?",
        endChatButton: "Da, încheie această conversație",
        goBackButton: "Nu, înapoi",
        endModalTitle: "Am reușit să vă rezolvăm problema?",
        yes: "Da",
        no: "Nu",
        submitButton: "Trimite",
        skipFeedbackButton: "Omite",
        additionalFeedbackPlaceholder:
          "Adăugați comentarii suplimentare aici...",
        consentAgreementAcceptButton: "Acceptă și începe",
        sourcesText: "Surse",
        submittingButtonText: "Se trimite...",
      };

    case "sk":
      return {
        start_again: "Začať znovu",
        default_company_name: "Názov spoločnosti",
        default_chatbot_subheading: "Náš bot odpovedá okamžite",
        input_placeholder: "Pošlite správu...",
        bottom_powered_by: "Poskytované",
        bottom_text_cta: "Vytvorte si vlastného AI bota s",
        default_welcome_message: "Ahoj, ako vám môžem pomôcť?",
        resetModalTitle: "Ste si istý, že chcete\nukončiť tento chat?",
        endChatButton: "Áno, ukončiť tento chat",
        goBackButton: "Nie, vrátiť sa späť",
        endModalTitle: "Podarilo sa nám vyriešiť váš problém?",
        yes: "Áno",
        no: "Nie",
        submitButton: "Odoslať",
        skipFeedbackButton: "Preskočiť",
        additionalFeedbackPlaceholder: "Pridajte ďalšie komentáre sem...",
        consentAgreementAcceptButton: "Prijať a začať",
        sourcesText: "Zdroje",
        submittingButtonText: "Odosielam...",
      };

    case "sl":
      return {
        start_again: "Začni znova",
        default_company_name: "Ime podjetja",
        default_chatbot_subheading: "Naš bot takoj odgovori",
        input_placeholder: "Pošlji sporočilo...",
        bottom_powered_by: "Omogoča",
        bottom_text_cta: "Make AI bot na yu sef wit",
        default_welcome_message: "Zdravo, kako vam lahko pomagam?",
        resetModalTitle: "Ali ste prepričani, da želite\nkončati ta klepet?",
        endChatButton: "Da, končaj ta klepet",
        goBackButton: "Ne, pojdi nazaj",
        endModalTitle: "Ali smo uspeli rešiti vaš problem?",
        yes: "Da",
        no: "Ne",
        submitButton: "Pošlji",
        skipFeedbackButton: "Preskoči",
        additionalFeedbackPlaceholder: "Dodajte dodatne komentarje tukaj...",
        consentAgreementAcceptButton: "Sprejmi in začni",
        sourcesText: "Viri",
        submittingButtonText: "Pošiljam...",
      };

    case "sv":
      return {
        start_again: "Börja om",
        default_company_name: "Företagsnamn",
        default_chatbot_subheading: "Vår bot svarar omedelbart",
        input_placeholder: "Skicka ett meddelande...",
        bottom_powered_by: "Tillhandahålls av",
        bottom_text_cta: "Bygg din egen AI-bot med",
        default_welcome_message: "Hej, hur kan jag hjälpa till?",
        resetModalTitle: "Är du säker på att du vill\navsluta den här chatten?",
        endChatButton: "Ja, avsluta den här chatten",
        goBackButton: "Nej, gå tillbaka",
        endModalTitle: "Kunde vi hjälpa till att lösa ditt problem?",
        yes: "Ja",
        no: "Nej",
        submitButton: "Skicka",
        skipFeedbackButton: "Hoppa över",
        additionalFeedbackPlaceholder:
          "Lägg till ytterligare kommentarer här...",
        consentAgreementAcceptButton: "Godkänn och starta",
        sourcesText: "Källor",
        submittingButtonText: "Skickar...",
      };

    case "fi":
      return {
        start_again: "Aloita uudelleen",
        default_company_name: "Yrityksen nimi",
        default_chatbot_subheading: "Bottimme vastaa välittömästi",
        input_placeholder: "Lähetä viesti...",
        bottom_powered_by: "Tarjoaa",
        bottom_text_cta: "Rakenna oma tekoälybottisi käyttäen",
        default_welcome_message: "Hei, kuinka voin auttaa?",
        resetModalTitle: "Haluatko varmasti\nlopettaa tämän chatin?",
        endChatButton: "Kyllä, lopeta tämä chat",
        goBackButton: "Ei, mene takaisin",
        endModalTitle: "Onnistuimmeko ratkaisemaan ongelmasi?",
        yes: "Kyllä",
        no: "Ei",
        submitButton: "Lähetä",
        skipFeedbackButton: "Ohita",
        additionalFeedbackPlaceholder: "Lisää lisäkommentteja tähän...",
        consentAgreementAcceptButton: "Hyväksy ja aloita",
        sourcesText: "Lähteet",
        submittingButtonText: "Lähetetään...",
      };

    case "et":
      return {
        start_again: "Alusta uuesti",
        default_company_name: "Ettevõtte nimi",
        default_chatbot_subheading: "Meie bot vastab kohe",
        input_placeholder: "Saada sõnum...",
        bottom_powered_by: "Pakub",
        bottom_text_cta: "Loo oma AI-bot koos",
        default_welcome_message: "Tere, kuidas saan sind aidata?",
        resetModalTitle:
          "Kas olete kindel, et soovite\nselle vestluse lõpetada?",
        endChatButton: "Jah, lõpeta see vestlus",
        goBackButton: "Ei, mine tagasi",
        endModalTitle: "Kas suutsime teie probleemi lahendada?",
        yes: "Jah",
        no: "Ei",
        submitButton: "Esita",
        skipFeedbackButton: "Jäta vahele",
        additionalFeedbackPlaceholder: "Lisa siia täiendavaid kommentaare...",
        consentAgreementAcceptButton: "Nõustu ja alusta",
        sourcesText: "Allikad",
        submittingButtonText: "Saadan...",
      };

    case "pt":
      return {
        start_again: "Reiniciar",
        default_company_name: "Nome da Empresa",
        default_chatbot_subheading: "Nosso bot responde instantaneamente",
        input_placeholder: "Enviar uma mensagem...",
        bottom_powered_by: "Desenvolvido por",
        bottom_text_cta: "Crie seu próprio bot de IA com",
        default_welcome_message: "Olá, como posso ajudá-lo?",
        endModalTitle: "Conseguimos resolver o seu problema?",
        yes: "Sim",
        no: "Não",
        submitButton: "Submeter",
        skipFeedbackButton: "Ignorar",
        additionalFeedbackPlaceholder:
          "Adicione comentários adicionais aqui...",
        consentAgreementAcceptButton: "Aceitar e iniciar",
        sourcesText: "Fontes",
        submittingButtonText: "Enviando...",
      };

    case "pt-pt":
      return {
        start_again: "Recomeçar",
        default_company_name: "Nome da Empresa",
        default_chatbot_subheading: "Nosso bot responde instantaneamente",
        input_placeholder: "Enviar mensagem...",
        bottom_powered_by: "Alimentado por",
        bottom_text_cta: "Crie o seu próprio bot de IA com",
        default_welcome_message: "Olá, como posso ajudar?",
        resetModalTitle: "Tem certeza de que\nquer terminar este chat?",
        endChatButton: "Sim, terminar este chat",
        goBackButton: "Não, voltar",
        endModalTitle: "Conseguimos resolver o seu problema?",
        yes: "Sim",
        no: "Não",
        submitButton: "Submeter",
        skipFeedbackButton: "Ignorar",
        additionalFeedbackPlaceholder:
          "Adicione comentários adicionais aqui...",
        consentAgreementAcceptButton: "Aceitar e começar",
        sourcesText: "Fontes",
        submittingButtonText: "A submeter...",
      };

    case "pt-br":
      return {
        start_again: "Reiniciar",
        default_company_name: "Nome da Empresa",
        default_chatbot_subheading: "Nosso bot responde instantaneamente",
        input_placeholder: "Digite uma mensagem...",
        bottom_powered_by: "Oferecido por",
        bottom_text_cta: "Crie seu próprio bot de IA com",
        default_welcome_message: "Olá, como posso ajudar?",
        resetModalTitle: "Tem certeza de que\nquer encerrar este chat?",
        endChatButton: "Sim, encerrar este chat",
        goBackButton: "Não, voltar",
        endModalTitle: "Conseguimos resolver o seu problema?",
        yes: "Sim",
        no: "Não",
        submitButton: "Enviar",
        skipFeedbackButton: "Pular",
        additionalFeedbackPlaceholder:
          "Adicione comentários adicionais aqui...",
        consentAgreementAcceptButton: "Aceitar e iniciar",
        sourcesText: "Fontes",
        submittingButtonText: "Enviando...",
      };

    // Also case "en":
    default:
      return {
        start_again: "Start again",
        default_company_name: "Company Name",
        default_chatbot_subheading: "Our bot answers instantly",
        input_placeholder: "Send a message...",
        bottom_powered_by: "Powered by",
        bottom_text_cta: "Build your own AI bot with",
        default_welcome_message: "Hey there, how can I help you?",
        loading: "Loading...",
        resetModalTitle: "Are you sure you\nwant to end this chat?",
        endChatButton: "Yes, end this chat",
        goBackButton: "No, go back",
        endModalTitle: "Did we help resolve your issue?",
        yes: "Yes",
        no: "No",
        submitButton: "Submit",
        skipFeedbackButton: "Skip",
        additionalFeedbackPlaceholder: "Add additional comments here...",
        consentAgreementAcceptButton: "Accept and Start",
        sourcesText: "Sources",
        submittingButtonText: "Submitting...",
        requestCallbackPlaceholder: "Request a callback",
        formAlreadySubmittedMessage:
          "Thank you for requesting a callback. We’ve shared your details with the team. Someone will get in touch with you soon.",
      };
  }
};
/* eslint-enable max-len */

export function removePeriodsAfterURL(text: string) {
  const urlRegex = /(https?:\/\/\S+)/g;
  const urlMatches = text.match(urlRegex);

  if (urlMatches) {
    for (const url of urlMatches) {
      const lastIndex = url.length - 1;
      if (url[lastIndex] === ".") {
        const urlWithoutPeriod = url.slice(0, Math.max(0, lastIndex));
        text = text.replace(url, urlWithoutPeriod);
      }
    }
  }

  return text;
}
